/* =========================================================================
   <zoom-scroll>
   Pinned, scroll-linked zoom story. As the block pins to the viewport, the
   page's own scroll position drives a zoom-cross-fade between full-width
   slides. A small rAF lerp smooths the progress value so the transitions feel
   buttery without hijacking scroll or pulling in an external smooth-scroll lib.
   ========================================================================= */
(function () {
  'use strict';

  if (customElements.get('zoom-scroll')) return;

  var hasTheme = typeof window.theme === 'object' && window.theme !== null;
  var throttle =
    hasTheme && theme.utils && theme.utils.throttle
      ? theme.utils.throttle
      : function (fn) {
          return fn;
        };

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  // smoothstep — crisper opacity crossover than a linear fade
  function ease(t) {
    return t * t * (3 - 2 * t);
  }

  function reducedMotion() {
    if (hasTheme && theme.config && typeof theme.config.motionReduced === 'boolean') {
      return theme.config.motionReduced;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class ZoomScroll extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.setup();
      }
      if (!this.ready) return;

      if (this.mql.addEventListener) {
        this.mql.addEventListener('change', this.evaluateMode);
        this.rmql.addEventListener('change', this.evaluateMode);
      } else if (this.mql.addListener) {
        this.mql.addListener(this.evaluateMode);
        this.rmql.addListener(this.evaluateMode);
      }
      this.evaluateMode();
    }

    disconnectedCallback() {
      if (this.mql) {
        if (this.mql.removeEventListener) {
          this.mql.removeEventListener('change', this.evaluateMode);
        } else if (this.mql.removeListener) {
          this.mql.removeListener(this.evaluateMode);
        }
      }
      if (this.rmql) {
        if (this.rmql.removeEventListener) {
          this.rmql.removeEventListener('change', this.evaluateMode);
        } else if (this.rmql.removeListener) {
          this.rmql.removeListener(this.evaluateMode);
        }
      }
      this.disable();

      // Free the audio graph for good when the element leaves the DOM.
      if (this._suspendTimer) {
        clearTimeout(this._suspendTimer);
        this._suspendTimer = null;
      }
      if (this.audioCtx && typeof this.audioCtx.close === 'function') {
        try {
          this.audioCtx.close();
        } catch (e) {}
      }
    }

    setup() {
      this.viewport = this.querySelector('.zoom-scroll__viewport');
      this.slides = Array.prototype.slice.call(
        this.querySelectorAll('.zoom-scroll__slide')
      );
      this.count = this.slides.length;
      this.dots = Array.prototype.slice.call(
        this.querySelectorAll('.zoom-scroll__dot')
      );
      this.progressBar = this.querySelector('.zoom-scroll__progress-bar');

      if (!this.viewport || this.count < 2) {
        this.ready = false;
        return;
      }

      this.layers = this.slides.map(function (slide) {
        return {
          slide: slide,
          bg: slide.querySelector('.zoom-scroll__bg'),
          content: slide.querySelector('.zoom-scroll__content'),
        };
      });

      this.zoom = parseFloat(this.dataset.zoom);
      if (!(this.zoom >= 0)) this.zoom = 0.2;
      // Backgrounds only ever scale up (>= 1) so blurred/zoomed edges never
      // reveal the slide behind — keep that movement subtle.
      this.bgZoom = Math.min(this.zoom, 0.12);

      this.smoothing = parseFloat(this.dataset.smoothing);
      if (!(this.smoothing > 0 && this.smoothing <= 1)) this.smoothing = 0.1;

      this.disableMobile = this.dataset.disableMobile === 'true';
      this.ignoreReducedMotion = this.dataset.ignoreReducedMotion === 'true';

      this.current = 0;
      this.target = 0;
      this.activeIndex = -1;
      this.total = 1;
      this.running = false;
      this.rafId = null;
      this.enabled = null;
      // When true the section still pins and switches slides on scroll, but
      // drops the zoom + smoothing so slides swap instantly (reduced motion).
      this.reducedMotion = false;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = throttle(this.measure.bind(this));
      this.tick = this.tick.bind(this);
      this.evaluateMode = this.evaluateMode.bind(this);

      this.dots.forEach(
        function (dot, index) {
          dot.addEventListener(
            'click',
            function () {
              this.scrollToIndex(index);
            }.bind(this)
          );
        }.bind(this)
      );

      // Audio is a bonus layer — never let it block the core pinned behaviour.
      this.audioEnabled = false;
      this.hasAudio = false;
      try {
        this.setupAudio();
      } catch (e) {
        this.audioEnabled = false;
        this.hasAudio = false;
      }

      this.mql = window.matchMedia('(min-width: 768px)');
      this.rmql = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.ready = true;
    }

    /* ------------------------------------------------------------------ *
     *  Audio
     *  - "synced": all clips play together through the Web Audio API from one
     *    shared, gaplessly-looping clock; only the active slide's clip is
     *    audible. Because they share a start time they stay perfectly aligned,
     *    so scrolling between slides cross-fades layers of one continuous song.
     *    (Falls back to the <audio> elements where Web Audio is unavailable.)
     *  - "separate": only the active slide's clip plays, restarting from 0
     *    each time that slide becomes active (looping optional).
     *  Browsers block audio until a gesture, so nothing plays until the
     *  visitor taps the sound toggle.
     * ------------------------------------------------------------------ */
    setupAudio() {
      this.audioEnabled = this.dataset.audio === 'true';
      this.audioEls = [];
      this.hasAudio = false;
      this.soundOn = false;
      this.audioStarted = false;
      this.inView = true;
      this.audioActiveIndex = 0;
      // Shared playback clock for "synced" mode: the position the audible clip
      // is at, handed from one slide's clip to the next as you scroll.
      this.sharedTime = 0;

      if (!this.audioEnabled) return;

      this.audioMode = this.dataset.audioMode === 'separate' ? 'separate' : 'synced';
      this.audioVolume = parseFloat(this.dataset.audioVolume);
      if (!(this.audioVolume >= 0 && this.audioVolume <= 1)) this.audioVolume = 0.8;

      // Synced mode plays through the Web Audio API: every clip is decoded into
      // a buffer and started from one shared clock with sample-accurate (gapless)
      // looping, so loops are seamless and the clips can never drift out of
      // alignment. Switching slides only cross-fades gain nodes. Separate mode
      // (and any browser without Web Audio) falls back to the <audio> elements.
      var AudioCtx = window.AudioContext || window.webkitAudioContext;
      this.useWebAudio = this.audioMode === 'synced' && !!AudioCtx;
      this.AudioCtx = AudioCtx;
      this.audioCtx = null;
      this.audioMaster = null;
      this.audioBuffers = [];
      this.audioSources = [];
      this.audioGains = [];
      this.audioGraphStarted = false;
      this._webAudioInit = null;
      this._suspendTimer = null;

      var container = this.querySelector('.zoom-scroll__audio');
      if (container) {
        Array.prototype.forEach.call(
          container.querySelectorAll('audio'),
          function (el) {
            var index = parseInt(el.dataset.index, 10);
            if (!isNaN(index)) {
              el.volume = 0;
              // Web Audio fetches the clips itself; don't let the element
              // download them a second time (it's only a fallback now).
              if (this.useWebAudio) el.preload = 'none';
              this.audioEls[index] = el;
              this.hasAudio = true;
            }
          }.bind(this)
        );
      }

      this.soundButton = this.querySelector('.zoom-scroll__sound');
      if (this.soundButton && this.hasAudio) {
        this.soundButton.addEventListener('click', this.toggleSound.bind(this));
      }

      if (this.hasAudio && 'IntersectionObserver' in window) {
        this.audioObserver = new IntersectionObserver(
          function (entries) {
            this.inView = entries[0].isIntersecting;
            if (!this.inView) this.pauseAudio();
            else if (this.soundOn) this.startAudio();
            this.updateSoundButton();
          }.bind(this),
          { threshold: 0 }
        );
      }

      this.onVisibility = function () {
        if (document.hidden) this.pauseAudio();
        else if (this.soundOn && this.inView && this.enabled) this.startAudio();
      }.bind(this);
    }

    audioForEach(fn) {
      for (var i = 0; i < this.audioEls.length; i++) {
        if (this.audioEls[i]) fn(this.audioEls[i], i);
      }
    }

    playEl(el) {
      var p = el.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    toggleSound() {
      if (!this.hasAudio) return;
      this.soundOn = !this.soundOn;
      if (this.soundOn) this.startAudio();
      else this.pauseAudio();
      this.updateSoundButton();
    }

    startAudio() {
      if (!this.hasAudio || !this.soundOn || !this.inView || !this.enabled) return;

      var active = this.activeIndex >= 0 ? this.activeIndex : 0;
      this.audioActiveIndex = active;
      var vol = this.audibleVolume();

      if (this.useWebAudio) {
        var self = this;
        this.ensureWebAudio()
          .then(function () {
            // The visitor may have toggled sound off / scrolled away while the
            // clips were decoding — bail if we're no longer meant to play.
            if (!self.soundOn || !self.inView || !self.enabled) return;
            self.startWebAudioGraph();
            self.resumeWebAudio();
            self.setWebAudioActive(self.audioActiveIndex, 160);
          })
          .catch(function () {
            // Decoding/Web Audio failed — fall back to the <audio> elements.
            self.useWebAudio = false;
            self.startAudio();
          });
        this.audioStarted = true;
        return;
      }

      if (this.audioMode === 'separate') {
        // Only the active clip plays, from the beginning.
        this.audioForEach(function (el, i) {
          if (i !== active) el.pause();
        });
        var sep = this.audioEls[active];
        if (sep) {
          if (!this.audioStarted) {
            try { sep.currentTime = 0; } catch (e) {}
          }
          this.fadeAudio(sep, vol, 120);
          this.playEl(sep);
        }
      } else {
        // Synced: only the active clip is audible; it carries the shared
        // timeline. Everything else stays paused and silent.
        this.audioForEach(function (el, i) {
          if (i !== active) {
            el.pause();
            el.volume = 0;
          }
        });
        var act = this.audioEls[active];
        if (act) {
          if (!this.audioStarted) {
            try { act.currentTime = this.sharedTime || 0; } catch (e) {}
          }
          this.fadeAudio(act, vol, 150);
          this.playEl(act);
        }
      }
      this.audioStarted = true;
    }

    pauseAudio() {
      if (this.useWebAudio) {
        this.suspendWebAudio();
        return;
      }
      this.audioForEach(function (el) {
        if (el.__zsFade) {
          cancelAnimationFrame(el.__zsFade);
          el.__zsFade = null;
        }
        el.pause();
      });
    }

    /* ---- Web Audio (synced mode) -------------------------------------- *
     * All clips decode up front and play together from one shared clock,
     * each looping its own buffer gaplessly. Only the active slide's gain
     * is raised, so switching slides is a pure cross-fade — the clips share
     * a start time and can never drift apart. A master gain handles overall
     * volume and click-free mute/unmute via the context suspend/resume.
     * ------------------------------------------------------------------- */
    ensureWebAudio() {
      if (this._webAudioInit) return this._webAudioInit;
      if (!this.AudioCtx) {
        this._webAudioInit = Promise.reject(new Error('No Web Audio'));
        return this._webAudioInit;
      }

      var self = this;
      try {
        this.audioCtx = new this.AudioCtx();
      } catch (e) {
        this._webAudioInit = Promise.reject(e);
        return this._webAudioInit;
      }

      this.audioMaster = this.audioCtx.createGain();
      this.audioMaster.gain.value = this.audioVolume;
      this.audioMaster.connect(this.audioCtx.destination);

      var ctx = this.audioCtx;
      var jobs = [];
      this.audioForEach(function (el, i) {
        var url = el.getAttribute('src');
        if (!url) return;
        jobs.push(
          fetch(url)
            .then(function (r) {
              return r.arrayBuffer();
            })
            .then(function (data) {
              // Promise form isn't universal (older Safari) — wrap the callback
              // form so both work.
              return new Promise(function (resolve, reject) {
                ctx.decodeAudioData(data, resolve, reject);
              });
            })
            .then(function (buffer) {
              self.audioBuffers[i] = buffer;
            })
            .catch(function () {
              // Leave this slot empty; that slide just stays silent.
            })
        );
      });

      this._webAudioInit = Promise.all(jobs).then(function () {
        // Bail to the fallback if nothing decoded at all.
        var any = false;
        for (var i = 0; i < self.audioBuffers.length; i++) {
          if (self.audioBuffers[i]) {
            any = true;
            break;
          }
        }
        if (!any) throw new Error('No audio decoded');
      });
      return this._webAudioInit;
    }

    startWebAudioGraph() {
      if (this.audioGraphStarted || !this.audioCtx) return;
      var ctx = this.audioCtx;
      // Start every clip at the same instant so they stay phase-locked.
      var startAt = ctx.currentTime + 0.06;
      for (var i = 0; i < this.audioBuffers.length; i++) {
        var buffer = this.audioBuffers[i];
        if (!buffer) continue;
        var source = ctx.createBufferSource();
        source.buffer = buffer;
        source.loop = true; // sample-accurate, gapless
        var gain = ctx.createGain();
        gain.gain.value = 0; // every clip silent until selected
        source.connect(gain).connect(this.audioMaster);
        try {
          source.start(startAt);
        } catch (e) {}
        this.audioSources[i] = source;
        this.audioGains[i] = gain;
      }
      this.audioGraphStarted = true;
    }

    // Raise the active clip's gain to 1 and drop the rest to 0 (cross-fade).
    setWebAudioActive(index, fadeMs) {
      if (!this.audioGraphStarted || !this.audioCtx) return;
      var now = this.audioCtx.currentTime;
      var dur = (fadeMs > 0 ? fadeMs : 150) / 1000;
      for (var i = 0; i < this.audioGains.length; i++) {
        var gain = this.audioGains[i];
        if (!gain) continue;
        var to = i === index ? 1 : 0;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(gain.gain.value, now);
        gain.gain.linearRampToValueAtTime(to, now + dur);
      }
    }

    fadeMaster(to, ms) {
      if (!this.audioCtx || !this.audioMaster) return;
      var now = this.audioCtx.currentTime;
      var g = this.audioMaster.gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(clamp(to, 0, 1), now + (ms > 0 ? ms : 0) / 1000);
    }

    resumeWebAudio() {
      if (!this.audioCtx) return;
      if (this._suspendTimer) {
        clearTimeout(this._suspendTimer);
        this._suspendTimer = null;
      }
      var self = this;
      var fadeIn = function () {
        self.fadeMaster(self.audioVolume, 120);
      };
      if (this.audioCtx.state === 'suspended') {
        var p = this.audioCtx.resume();
        if (p && typeof p.then === 'function') p.then(fadeIn, function () {});
        else fadeIn();
      } else {
        fadeIn();
      }
    }

    suspendWebAudio() {
      if (!this.audioCtx || this.audioCtx.state !== 'running') return;
      // Fade out first so suspending doesn't click, then actually suspend.
      this.fadeMaster(0, 120);
      var self = this;
      if (this._suspendTimer) clearTimeout(this._suspendTimer);
      this._suspendTimer = setTimeout(function () {
        self._suspendTimer = null;
        if (self.audioCtx && self.audioCtx.state === 'running') {
          self.audioCtx.suspend();
        }
      }, 150);
    }

    // Target volume for the clip that should currently be heard.
    audibleVolume() {
      return this.soundOn && this.inView && this.enabled ? this.audioVolume : 0;
    }

    // Tween an element's volume so hand-offs and mute/unmute don't click.
    fadeAudio(el, to, ms, onDone) {
      if (!el) return;
      if (el.__zsFade) {
        cancelAnimationFrame(el.__zsFade);
        el.__zsFade = null;
      }
      to = clamp(to, 0, 1);
      var from = el.volume;
      if (!(ms > 0) || Math.abs(to - from) < 0.001) {
        el.volume = to;
        if (onDone) onDone();
        return;
      }
      var start = null;
      var step = function (now) {
        if (start === null) start = now;
        var p = clamp((now - start) / ms, 0, 1);
        el.volume = clamp(from + (to - from) * p, 0, 1);
        if (p < 1) {
          el.__zsFade = requestAnimationFrame(step);
        } else {
          el.__zsFade = null;
          if (onDone) onDone();
        }
      };
      el.__zsFade = requestAnimationFrame(step);
    }

    onActiveAudioChange(next) {
      if (!this.hasAudio) return;
      var prev = this.audioActiveIndex;
      this.audioActiveIndex = next;
      var audible = this.soundOn && this.inView && this.enabled;

      if (this.useWebAudio) {
        // All clips already play in sync; just cross-fade to the new one.
        if (this.audioGraphStarted) this.setWebAudioActive(next, 160);
        return;
      }

      if (this.audioMode === 'separate') {
        // Restart the newly active clip from the beginning.
        this.audioForEach(function (el, i) {
          if (i !== next) el.pause();
        });
        var sep = this.audioEls[next];
        if (sep) {
          try { sep.currentTime = 0; } catch (e) {}
          if (audible) {
            this.fadeAudio(sep, this.audibleVolume(), 120);
            this.playEl(sep);
          } else {
            sep.volume = 0;
          }
        }
        return;
      }

      // Synced: hand the shared timeline from the outgoing clip to the incoming
      // one so it resumes at the same second (works in both directions).
      var prevEl = this.audioEls[prev];
      var nextEl = this.audioEls[next];

      // Silence anything that isn't part of this hand-off (e.g. fast scrolling
      // across several slides at once).
      this.audioForEach(function (el, i) {
        if (i !== next && i !== prev) {
          if (el.__zsFade) {
            cancelAnimationFrame(el.__zsFade);
            el.__zsFade = null;
          }
          el.volume = 0;
          el.pause();
        }
      });

      var t = this.sharedTime || 0;
      if (prevEl) t = prevEl.currentTime;
      this.sharedTime = t;

      if (nextEl && nextEl !== prevEl) {
        try {
          var dur = nextEl.duration;
          nextEl.currentTime = dur && isFinite(dur) && dur > 0 ? t % dur : t;
        } catch (e) {}
      }

      if (audible) {
        if (nextEl) {
          this.fadeAudio(nextEl, this.audibleVolume(), 150);
          this.playEl(nextEl);
        }
        if (prevEl && prevEl !== nextEl) {
          this.fadeAudio(
            prevEl,
            0,
            150,
            function () {
              // Only pause if it hasn't become the active clip again meanwhile.
              if (this.audioActiveIndex !== prev) prevEl.pause();
            }.bind(this)
          );
        }
      } else {
        if (prevEl && prevEl !== nextEl) prevEl.pause();
        if (nextEl) nextEl.volume = 0;
      }
    }

    updateSoundButton() {
      if (!this.soundButton) return;
      var show = this.audioEnabled && this.hasAudio && this.enabled && this.inView;
      this.soundButton.hidden = !show;
      this.soundButton.classList.toggle('is-on', this.soundOn);
      this.soundButton.setAttribute('aria-pressed', this.soundOn ? 'true' : 'false');
      this.soundButton.setAttribute(
        'aria-label',
        this.soundOn ? 'Mute sound' : 'Play sound'
      );
      var label = this.soundButton.querySelector('.zoom-scroll__sound-label');
      if (label) label.textContent = this.soundOn ? 'Sound on' : 'Sound off';
    }

    evaluateMode() {
      // Pinning is only switched off for the simple stacked mobile layout;
      // reduced motion still pins but renders without zoom/smoothing.
      var allowPin = !this.disableMobile || this.mql.matches;
      if (!allowPin) {
        this.disable();
        return;
      }

      // Read the live media query (not the theme's load-time snapshot) so the
      // prefers-reduced-motion listener actually responds to runtime changes.
      var reduce = this.ignoreReducedMotion
        ? false
        : this.rmql
          ? this.rmql.matches
          : reducedMotion();
      var changed = this.reducedMotion !== reduce;
      this.reducedMotion = reduce;
      this.enable();
      // If only the motion preference flipped while already pinned, re-apply
      // the current position instantly under the new setting.
      if (changed && this.enabled === true) {
        this.current = this.target;
        this.render();
      }
    }

    enable() {
      if (this.enabled === true) return;
      this.enabled = true;
      this.classList.add('is-pinned');
      this.measure();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.activeIndex = -1;
      this.onScroll();

      if (this.audioEnabled && this.hasAudio) {
        try {
          if (this.audioObserver) this.audioObserver.observe(this);
          document.addEventListener('visibilitychange', this.onVisibility);
          this.updateSoundButton();
        } catch (e) {}
      }
    }

    disable() {
      if (this.enabled === false) return;
      this.enabled = false;
      this.classList.remove('is-pinned');
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.stop();
      this.reset();

      if (this.audioEnabled && this.hasAudio) {
        try {
          if (this.audioObserver) this.audioObserver.unobserve(this);
          document.removeEventListener('visibilitychange', this.onVisibility);
          this.pauseAudio();
          this.updateSoundButton();
        } catch (e) {}
      }
    }

    reset() {
      this.layers.forEach(function (layer) {
        layer.slide.style.opacity = '';
        layer.slide.style.pointerEvents = '';
        if (layer.bg) layer.bg.style.transform = '';
        if (layer.content) layer.content.style.transform = '';
      });
      if (this.progressBar) this.progressBar.style.transform = '';
    }

    measure() {
      this.viewportHeight = this.viewport.offsetHeight;
      this.total = Math.max(this.offsetHeight - this.viewportHeight, 1);
      this.onScroll();
    }

    onScroll() {
      var rect = this.getBoundingClientRect();
      var scrolled = clamp(-rect.top, 0, this.total);
      var progress = scrolled / this.total;
      this.target = progress * (this.count - 1);
      this.start();
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.rafId = requestAnimationFrame(this.tick);
    }

    stop() {
      this.running = false;
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    tick() {
      if (!this.running) return;

      // Reduced motion: no smoothing — snap straight to the scroll position so
      // slides swap instantly with no glide.
      if (this.reducedMotion) {
        this.current = this.target;
        this.render();
        this.running = false;
        this.rafId = null;
        return;
      }

      var diff = this.target - this.current;
      if (Math.abs(diff) < 0.0005) {
        this.current = this.target;
        this.render();
        this.running = false;
        this.rafId = null;
        return;
      }

      this.current = lerp(this.current, this.target, this.smoothing);
      this.render();
      this.rafId = requestAnimationFrame(this.tick);
    }

    render() {
      var pos = this.current;
      var active = clamp(Math.round(pos), 0, this.count - 1);

      if (this.reducedMotion) {
        // Instant swap: the nearest slide is shown, the rest hidden — a hard
        // cut at the midpoint, with no zoom and no cross-fade.
        for (var i = 0; i < this.count; i++) {
          var rl = this.layers[i];
          var on = i === active;
          var op = on ? '1' : '0';
          if (rl.slide.style.opacity !== op) {
            rl.slide.style.opacity = op;
            rl.slide.style.pointerEvents = on ? 'auto' : 'none';
          }
          // Clear any transforms left over from a previous animated frame.
          if (rl.bg && rl.bg.style.transform) rl.bg.style.transform = '';
          if (rl.content && rl.content.style.transform) rl.content.style.transform = '';
        }
      } else {
        for (var k = 0; k < this.count; k++) {
          var layer = this.layers[k];
          var delta = pos - k; // <0 = incoming, >0 = already passed
          var ad = Math.abs(delta);

          if (ad >= 1) {
            if (layer.slide.style.opacity !== '0') {
              layer.slide.style.opacity = '0';
              layer.slide.style.pointerEvents = 'none';
            }
            continue;
          }

          var o = ease(1 - ad);
          layer.slide.style.opacity = o.toFixed(3);
          layer.slide.style.pointerEvents = ad < 0.5 ? 'auto' : 'none';

          if (layer.content) {
            // Directional zoom-through: passed slides scale up & out, incoming
            // slides grow from smaller as they arrive.
            var contentScale = 1 + delta * this.zoom;
            layer.content.style.transform = 'scale(' + contentScale.toFixed(4) + ')';
          }

          if (layer.bg) {
            // Settles to scale 1 when active; zooms in (>= 1) at either edge.
            var bgScale = 1 + ad * this.bgZoom;
            layer.bg.style.transform = 'scale(' + bgScale.toFixed(4) + ')';
          }
        }
      }

      if (active !== this.activeIndex) {
        this.activeIndex = active;
        for (var d = 0; d < this.dots.length; d++) {
          var isActive = d === active;
          this.dots[d].classList.toggle('is-active', isActive);
          this.dots[d].setAttribute('aria-current', isActive ? 'true' : 'false');
        }
        if (this.hasAudio) {
          try {
            this.onActiveAudioChange(active);
          } catch (e) {}
        }
      }

      if (this.progressBar) {
        var frac = this.count > 1 ? pos / (this.count - 1) : 0;
        this.progressBar.style.transform = 'scaleX(' + clamp(frac, 0, 1).toFixed(4) + ')';
      }
    }

    scrollToIndex(index) {
      if (!this.enabled) return;
      var elementTopDoc = this.getBoundingClientRect().top + window.scrollY;
      var targetScrolled =
        this.count > 1 ? (index / (this.count - 1)) * this.total : 0;
      window.scrollTo({
        top: elementTopDoc + targetScrolled,
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    }
  }

  customElements.define('zoom-scroll', ZoomScroll);
})();
